import { Component, OnInit } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faChalkboardUser,
  faCheckCircle,
  faCircleExclamation,
  faPlus,
  faPlusCircle,
  faSpinner,
  faTrashAlt,
  faTriangleExclamation,
  faPencil,
} from '@fortawesome/free-solid-svg-icons';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { getAcceptableRoles, UserRole } from '../../core/models/user.model';
import { Router, RouterLink } from '@angular/router';
import { ManagementUser, EditableManagementUser } from '../../core/models/management-user.model';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { UsersService } from '../../core/services/users.service';
import { NgClass } from '@angular/common';
import { UtilityService } from '../../core/services/utility.service';

type SchoolUserForm = {
  firstName: FormControl<string>,
  lastName: FormControl<string>,
  email: FormControl<string>,
  userRole: FormControl<UserRole | ''>,
} ;

type SchoolUsersForm = {
  users: FormArray<FormGroup<SchoolUserForm>>,
  notify: FormControl<boolean | null>,
};

@Component({
  selector: 'wf-school-users',
  standalone: true,
  imports: [
    FontAwesomeModule,
    ReactiveFormsModule,
    RouterLink,
    RouterLink,
    NgClass,
  ],
  templateUrl: './user.component.html',
  styleUrl: './user.component.css'
})
export class UserComponent implements OnInit {
  formTitle = 'Add School User' ;
  schoolUsersForm: FormGroup<SchoolUsersForm> ;
  userRoles: { key: string,  label: string }[] = [] ;
  currentUser: ManagementUser | null = null ;
  message = '' ;
  messageTitle = '' ;
  messageStatus = '' ;
  user: ManagementUser | null = null ;
  saving = false ;
  editMode = false ;

  schoolUsersIcon = faChalkboardUser;
  successIcon = faCheckCircle ;
  warningIcon = faTriangleExclamation ;
  errorIcon = faCircleExclamation ;
  loadingIcon = faSpinner ;
  addIcon = faPlusCircle ;
  trashIcon = faTrashAlt ;
  createIcon = faPlus ;
  editIcon = faPencil ;

  constructor(
    private router: Router,
    private usersService: UsersService,
    private sessionStorageService: SessionStorageService,
    private utilityService: UtilityService,
  ) {
    // If we are passed a user in our route state, then we are editing
    // TODO: Should we consider using a resolver if the user is not passed on state?
    const nav = this.router.getCurrentNavigation() ;
    const state = nav?.extras.state

    this.schoolUsersForm = new FormGroup<SchoolUsersForm>({
      users: new FormArray<FormGroup<SchoolUserForm>>([new FormGroup<SchoolUserForm>({
        firstName: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(50)] }),
        lastName: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(50)] }),
        email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
        userRole: new FormControl('', { nonNullable: true, validators: Validators.required }),
      })]),
      notify: new FormControl(false),
    }) ;

    if (state && state['user'])
    {
      this.user = state['user'] as ManagementUser ;
      this.formTitle = 'Edit School User' ;
      this.editMode = true ;

      this.users.at(0).patchValue({
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        email: this.user.email!,
        userRole: this.user.userRole as UserRole,
      }) ;
    }
  }

  ngOnInit(): void {
    this.currentUser = this.sessionStorageService.getUserData() ;
    this.userRoles = getAcceptableRoles(this.currentUser!.userRole).map((role) => {
      return {
        key: role,
        label: this.utilityService.formatUserRoleLabel(role),
      } ;
    }) ;

    // Set our default selected role
    this.users.at(0).controls.userRole.patchValue((this.user?.userRole ?? this.userRoles[0].key) as UserRole) ;
  }

  addSchoolUser() {
    this.users.push(new FormGroup<SchoolUserForm>({
      firstName: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(50)] }),
      lastName: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(50)] }),
      email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
      userRole: new FormControl(this.userRoles[0].key as UserRole, { nonNullable: true, validators: Validators.required }),
    })) ;
  }

  removeSchoolUser(index: number) {
    this.users.removeAt(index) ;
  }

  saveSchoolUsers() {
    this.saving = true ;

    const managementUsers: EditableManagementUser[] = this.users.getRawValue().map((user) => {
      return {
        userID: this.user?.userID ?? 0,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        username: user.email,
        userRole: user.userRole,
        districtID: this.currentUser!.districtID,
        schoolID: this.currentUser!.schoolID,
        enabled: true,
      };
    }) ;

    if (!this.editMode) {
      this.usersService.saveSchoolUsers(managementUsers, this.notify?.getRawValue() ?? false).subscribe({
        next: (data) => {
          const notified = this.notify?.getRawValue() ?? false;
          this.schoolUsersForm.reset();
          this.users.clear();
          this.users.push(new FormGroup<SchoolUserForm>({
            firstName: new FormControl('', {
              nonNullable: true,
              validators: [Validators.required, Validators.maxLength(50)]
            }),
            lastName: new FormControl('', {
              nonNullable: true,
              validators: [Validators.required, Validators.maxLength(50)]
            }),
            email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
            userRole: new FormControl(this.userRoles[0].key as UserRole, { nonNullable: true, validators: Validators.required }),
          }));

          if (data.existingAccounts.length) {
            this.messageStatus = 'warning';
            this.messageTitle = 'Some Accounts Created Successfully';
            this.message = 'Some of your School User Accounts were successfully created. ' +
              'The following accounts were skipped because existing accounts were already found:<ul>';

            data.existingAccounts.forEach((email) => {
              this.message += `<li>${email}</li>`;
            });

            this.message += '</ul>';
          } else {
            this.messageStatus = 'success';
            this.messageTitle = 'Accounts Create Successfully';
            this.message = 'Your School User Accounts were successfully created.';
            if (notified) {
              this.message += ' The users have been notified of their new WordFlight account.';
            }
          }

          this.saving = false;
        },
        error: (error) => {
          this.messageStatus = 'error';
          this.messageTitle = 'Accounts Not Created';
          this.message = 'An error occurred while trying to create your School User Accounts: ';
          if (error.error && error.error.message) {
            this.message += `<br>${error.error.message}`;
          }

          this.saving = false;
        }
      });
    }
    else
    {
      // We are in edit mode, so edit our single user
      this.usersService.editSchoolUser(managementUsers[0]).subscribe({
        next: () => {
          this.messageStatus = 'success';
          this.messageTitle = 'Account Edit Successfully';
          this.message = 'Your School User Account was successfully edited.';

          this.saving = false;
        },
        error: (error) => {
          this.messageStatus = 'error';
          this.messageTitle = 'Account Not Edited';
          this.message = 'An error occurred while trying to edit your School User Accounts: ';
          if (error.error && error.error.message) {
            this.message += `<br>${error.error.message}`;
          }

          this.saving = false;
        }
      })
    }
  }

  get users() {
    return this.schoolUsersForm.get('users') as FormArray<FormGroup<SchoolUserForm>> ;
  }

  get notify() {
    return this.schoolUsersForm.get('notify') ;
  }
}
